import React from 'react'
import {
    Col,
    CardHeader,
    Row,
} from 'reactstrap'
import classNames from 'classnames'

/** Bootstrap responsive breakpoint tokens */
type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

/** Tag type accepted by the internal Title component */
type TitleTag = keyof JSX.IntrinsicElements | React.ComponentType<{ className?: string; children?: React.ReactNode }>

interface TitleProps {
    /** Heading tag or component to render the title with */
    titleTag?: TitleTag
    /** Extra CSS classes on the heading element */
    className?: string
    /** Breakpoint at which `mb-0` is applied */
    breakPoint?: Breakpoint
    children?: React.ReactNode
}

const Title = ({
    titleTag: TitleTag = 'h5',
    className,
    breakPoint,
    children,
}: TitleProps) => (
    <TitleTag
        className={classNames(
            {
                'mb-0': !breakPoint,
                [`mb-${breakPoint}-0`]: !!breakPoint,
            },
            className,
        )}
    >
        {children}
    </TitleTag>
)

interface FalconCardHeaderProps {
    /** Card header title (required) */
    title: React.ReactNode
    /** Apply `bg-light` class to the card header */
    light?: boolean
    /** Heading tag or component used for the title */
    titleTag?: TitleTag
    /** Extra CSS classes on the title element */
    titleClass?: string
    /** Extra CSS classes on the `CardHeader` wrapper */
    className?: string
    /** Breakpoint for responsive column layout when `children` is provided */
    breakPoint?: Breakpoint
    /** Optional right-side slot content */
    children?: React.ReactNode
}

/**
 * Falcon-themed `CardHeader` with an optional right-side actions slot.
 * When `children` is provided the header splits into a two-column responsive layout.
 */
const FalconCardHeader = ({
    title,
    light = true,
    titleTag,
    titleClass,
    className,
    breakPoint,
    children,
}: FalconCardHeaderProps) => (
    <CardHeader className={classNames({ 'bg-light': light }, className)}>
        {children ? (
            <Row className='align-items-center'>
                <Col>
                    <Title
                        breakPoint={breakPoint}
                        titleTag={titleTag}
                        className={titleClass}
                    >
                        {title}
                    </Title>
                </Col>
                <Col
                    {...{ [breakPoint ? breakPoint : 'xs']: 'auto' }}
                    className={`text${breakPoint ? `-${breakPoint}` : ''}-right`}
                >
                    {children}
                </Col>
            </Row>
        ) : (
            <Title
                breakPoint={breakPoint}
                titleTag={titleTag}
                className={titleClass}
            >
                {title}
            </Title>
        )}
    </CardHeader>
)

export default FalconCardHeader
